import { Box, Typography, Stack, Button, Chip, CircularProgress, Input, Sheet, LinearProgress, IconButton, List, ListItem, ListItemButton, ListItemContent, ListItemDecorator, Tab, TabList, TabPanel, Tabs, Alert, Grid, Select, Option, Textarea, FormControl, FormLabel } from "@mui/joy";
import { useContext, useEffect, useState, useRef } from "react";
import { LayoutContext } from "../context";
import { APIGetModules, APIUploadPDF, APIBatchGenerateModules, APIGetEmptyModulesStats, APIBatchExpandModules, APISearchModulesContent, APIGenerateModuleContent } from "../api";
import { useNavigate, Link as RouterLink } from "react-router";
import { MagnifyingGlass, CaretRight, CheckCircle, Circle, BookOpen, Lock, LockKey, Sparkle, UploadSimple, ArrowsClockwise, Brain, ClockCounterClockwise, TextT, Stethoscope, ArrowRight } from "@phosphor-icons/react";


// ========================================
// Discipline Card (top level)
// ========================================
function DisciplineCard({ discipline, searchQuery, contentMatchIds, navigate }) {
    const [expanded, setExpanded] = useState(false);

    const hasSearchMatch = searchQuery && discipline.subsections.some(sub =>
        sub.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.topics.some(t =>
            t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            contentMatchIds.has(t.id)
        )
    );

    const isExpanded = expanded || hasSearchMatch;

    // Count filled modules across all subsections
    const filledCount = discipline.subsections.reduce((sum, sub) =>
        sum + sub.topics.filter(t => t.has_content).length, 0
    );
    const totalCount = discipline.subsections.reduce((sum, sub) =>
        sum + sub.topics.length, 0
    );
    const progress = totalCount > 0
        ? Math.round((filledCount / totalCount) * 100)
        : 0;

    return (
        <Sheet
            variant="outlined"
            sx={{
                borderRadius: 'xl',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
            }}
        >
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    px: 2.5,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'background.level1' },
                    transition: 'background 0.15s ease',
                }}
            >
                <Box sx={{
                    color: isExpanded ? 'primary.800' : 'text.secondary',
                    transition: 'transform 0.2s ease, color 0.2s ease',
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'flex',
                }}>
                    <CaretRight size={20} />
                </Box>

                <Typography level="title-md" sx={{ flex: 1, fontWeight: 600 }}>
                    {discipline.name}
                </Typography>

                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography level="body-xs" sx={{ color: filledCount === totalCount && totalCount > 0 ? '#B84C31' : 'text.tertiary' }}>
                        {filledCount}/{totalCount}
                    </Typography>
                    <LinearProgress
                        determinate
                        value={progress}
                        sx={{
                            width: 60,
                            '--LinearProgress-thickness': '4px',
                            '--LinearProgress-radius': '4px',
                        }}
                    />
                </Stack>
            </Box>

            {isExpanded && (
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    {discipline.subsections.map((subsection) => (
                        <SubsectionGroup
                            key={subsection.category_id}
                            subsection={subsection}
                            searchQuery={searchQuery}
                            contentMatchIds={contentMatchIds}
                            navigate={navigate}
                        />
                    ))}
                </Box>
            )}
        </Sheet>
    );
}


// ========================================
// Subsection Group (second level)
// ========================================
function SubsectionGroup({ subsection, searchQuery, contentMatchIds, navigate }) {
    const [expanded, setExpanded] = useState(false);

    const hasSearchMatch = searchQuery && subsection.topics.some(t =>
        t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contentMatchIds.has(t.id)
    );
    const nameMatch = searchQuery && subsection.name.toLowerCase().includes(searchQuery.toLowerCase());
    const isExpanded = expanded || hasSearchMatch || nameMatch;

    const readCount = subsection.topics.filter(t => t.is_read).length;
    const filledCount = subsection.topics.filter(t => t.has_content).length;
    const totalCount = subsection.topics.length;

    const filteredTopics = searchQuery
        ? subsection.topics.filter(t =>
            t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            nameMatch ||
            contentMatchIds.has(t.id)
        )
        : subsection.topics;

    if (searchQuery && filteredTopics.length === 0) return null;

    return (
        <Box>
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    px: 2.5,
                    pl: 5,
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderBottom: '1px solid',
                    borderColor: isExpanded ? 'divider' : 'transparent',
                    '&:hover': { bgcolor: 'background.level1' },
                    transition: 'all 0.15s ease',
                }}
            >
                <Box sx={{
                    color: 'text.tertiary',
                    transition: 'transform 0.2s ease',
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'flex',
                }}>
                    <CaretRight size={18} />
                </Box>

                <Typography level="body-sm" sx={{ flex: 1, fontWeight: 500 }}>
                    {subsection.name}
                </Typography>

                <Typography level="body-xs" sx={{ color: filledCount === totalCount ? '#B84C31' : 'text.tertiary' }}>
                    {filledCount}/{totalCount}
                </Typography>
            </Box>

            {isExpanded && (
                <List size="sm" sx={{ py: 0 }}>
                    {filteredTopics.map((topic) => (
                        <TopicItem
                            key={topic.id}
                            topic={topic}
                            navigate={navigate}
                            isTitleMatch={!!searchQuery && topic.title.toLowerCase().includes(searchQuery.toLowerCase())}
                            isContentMatch={contentMatchIds.has(topic.id) && searchQuery && !topic.title.toLowerCase().includes(searchQuery.toLowerCase())}
                        />
                    ))}
                </List>
            )}
        </Box>
    );
}


// ========================================
// Topic Item (leaf level)
// ========================================
function TopicItem({ topic, navigate, isTitleMatch, isContentMatch }) {
    const { openUpgradeModal } = useContext(LayoutContext);
    const isLocked = topic.trial_locked === true;

    // Правило из фидбека "никогда не оставлять empty state": lock'нутый модуль
    // кликается, но вместо перехода открывает UpgradeModal с понятным reason.
    const handleClick = () => {
        if (isLocked) {
            openUpgradeModal(`«${topic.title}» откроется после оформления полного доступа.`);
            return;
        }
        navigate(`/modules/${topic.id}`);
    };

    return (
        <ListItem sx={{ py: 0 }}>
            <ListItemButton
                onClick={handleClick}
                sx={{
                    py: 1,
                    pl: isTitleMatch ? 7 : 7.5,
                    pr: 2.5,
                    gap: 1.5,
                    borderRadius: 0,
                    bgcolor: isTitleMatch
                        ? 'rgba(218, 119, 86, 0.08)'
                        : isLocked ? 'rgba(184, 76, 49, 0.025)' : 'transparent',
                    borderLeft: isTitleMatch ? '3px solid #DA7756' : '3px solid transparent',
                    opacity: isLocked ? 0.72 : 1,
                    '&:hover': {
                        bgcolor: isLocked
                            ? 'rgba(184, 76, 49, 0.08)'
                            : (isTitleMatch ? 'rgba(218, 119, 86, 0.14)' : 'background.level1'),
                    },
                    transition: 'background 0.2s ease, opacity 0.2s ease',
                }}
            >
                <ListItemDecorator sx={{ minWidth: 24 }}>
                    {isLocked ? (
                        <LockKey size={16} color="#B84C31" weight="fill" />
                    ) : topic.is_read ? (
                        <CheckCircle size={18} color="#DA7756" weight="fill" />
                    ) : (
                        <Circle size={18} color={isTitleMatch ? '#DA7756' : undefined} />
                    )}
                </ListItemDecorator>
                <ListItemContent>
                    <Typography
                        level="body-sm"
                        sx={{
                            color: isTitleMatch
                                ? '#DA7756'
                                : isLocked
                                    ? 'text.tertiary'
                                    : (topic.has_content ? 'text.primary' : 'text.tertiary'),
                            fontWeight: isTitleMatch ? 600 : (topic.has_content ? 450 : 400),
                        }}
                    >
                        {topic.title}
                    </Typography>
                    {isContentMatch && (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.65rem' }}>
                            совпадение в тексте
                        </Typography>
                    )}
                </ListItemContent>
                {isLocked && (
                    <Chip
                        size="sm"
                        variant="soft"
                        color="danger"
                        sx={{ minHeight: 20, fontSize: '0.65rem', fontWeight: 600 }}
                    >
                        Открыть
                    </Chip>
                )}
                {!isLocked && topic.question_count > 0 && (
                    <Chip size="sm" variant="soft" sx={{ minHeight: 20, fontSize: '0.7rem' }}>
                        {topic.question_count}
                    </Chip>
                )}
            </ListItemButton>
        </ListItem>
    );
}


// ========================================
// Batch Generation Panel (admin only)
// ========================================
function BatchGenerationPanel({ token, onGenerated }) {
    const [emptyStats, setEmptyStats] = useState(null);
    const [pdfInfo, setPdfInfo] = useState(null);
    const [pdfPath, setPdfPath] = useState('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    // Config
    const [startPage, setStartPage] = useState(0);
    const [totalPages, setTotalPages] = useState(50);
    const [batchSize, setBatchSize] = useState(5);

    // Generation
    const [running, setRunning] = useState(false);
    const [results, setResults] = useState(null);

    // History
    const [genHistory, setGenHistory] = useState([]);

    const fileInputRef = useRef(null);

    useEffect(() => {
        loadStats();
        // Load history from localStorage
        try {
            const saved = localStorage.getItem('moduleBatchGenHistory');
            if (saved) setGenHistory(JSON.parse(saved));
        } catch (e) {
            console.error('Failed to parse gen history', e);
        }
    }, []);

    const saveGenHistory = (filename, start, pages, modulesFilled, duration) => {
        const entry = {
            filename,
            startPage: start,
            endPage: start + pages,
            pageCount: pages,
            modulesFilled,
            duration,
            date: new Date().toISOString(),
        };
        const updated = [entry, ...genHistory].slice(0, 3);
        localStorage.setItem('moduleBatchGenHistory', JSON.stringify(updated));
        setGenHistory(updated);
    };

    const loadStats = async () => {
        try {
            const res = await APIGetEmptyModulesStats(token);
            if (res.success) setEmptyStats(res);
        } catch (e) {
            console.error('Error loading stats:', e);
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setError('');
        try {
            const res = await APIUploadPDF(token, file);
            if (res.success) {
                setPdfPath(res.pdf_path);
                setPdfInfo({ filename: res.filename, totalPages: res.total_pages });
                // Auto-fill startPage from last history entry if same file
                const lastEntry = genHistory[0];
                if (lastEntry && lastEntry.filename === res.filename) {
                    const suggested = lastEntry.endPage;
                    if (suggested < res.total_pages) {
                        setStartPage(suggested);
                    } else {
                        setStartPage(0);
                    }
                } else {
                    setStartPage(0);
                }
                setTotalPages(Math.min(50, res.total_pages));
            }
        } catch (e) {
            setError('Ошибка загрузки: ' + (e.response?.data?.error || e.message));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleGenerate = async () => {
        if (!pdfPath) return;
        setRunning(true);
        setError('');
        setResults(null);
        try {
            const res = await APIBatchGenerateModules(token, {
                pdf_path: pdfPath,
                start_page: startPage,
                total_pages: totalPages,
                batch_size: batchSize,
            });
            setResults(res);
            saveGenHistory(
                pdfInfo.filename,
                startPage,
                totalPages,
                res.modules_filled || 0,
                res.total_duration_sec || 0
            );
            loadStats();
            if (onGenerated) onGenerated();
        } catch (e) {
            setError('Ошибка генерации: ' + (e.response?.data?.error || e.message));
        } finally {
            setRunning(false);
        }
    };

    const handleContinue = () => {
        setStartPage(startPage + totalPages);
        setResults(null);
    };

    const fillPercent = emptyStats ? Math.round((emptyStats.filled / emptyStats.total) * 100) : 0;

    return (
        <Stack spacing={2.5}>
            {/* Stats */}
            {emptyStats && (
                <Sheet variant="soft" sx={{ p: 2, px: 2.5, borderRadius: 'lg' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography level="body-sm" fontWeight={500}>
                            Модулей заполнено
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            {emptyStats.filled} из {emptyStats.total} ({fillPercent}%)
                        </Typography>
                    </Stack>
                    <LinearProgress
                        determinate
                        value={fillPercent}
                        sx={{
                            '--LinearProgress-thickness': '6px',
                            '--LinearProgress-radius': '6px',
                        }}
                    />
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
                        {emptyStats.empty} пустых модулей ожидают генерации
                    </Typography>
                </Sheet>
            )}

            {error && <Alert color="danger" variant="soft">{error}</Alert>}

            {/* Upload */}
            {!results && (
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'xl' }}>
                    <Typography level="title-sm" fontWeight={600} sx={{ mb: 2 }}>
                        1. Загрузите PDF
                    </Typography>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf"
                        style={{ display: 'none' }}
                        onChange={handleUpload}
                    />
                    <Button
                        variant="outlined"
                        color="neutral"
                        startDecorator={<UploadSimple size={18} />}
                        onClick={() => fileInputRef.current?.click()}
                        loading={uploading}
                        fullWidth
                    >
                        {pdfInfo ? `${pdfInfo.filename} (${pdfInfo.totalPages} стр.)` : 'Выбрать PDF файл'}
                    </Button>
                </Sheet>
            )}

            {/* History */}
            {!results && (
                <Sheet variant="soft" sx={{ p: 2, px: 2.5, borderRadius: 'lg' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <ClockCounterClockwise size={16} />
                        <Typography level="body-sm" fontWeight={600}>
                            Последние генерации
                        </Typography>
                    </Stack>
                    {genHistory.length === 0 ? (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            Генераций пока не было. Загрузите PDF и запустите генерацию.
                        </Typography>
                    ) : (
                        <Stack spacing={1}>
                            {genHistory.map((entry, i) => (
                                <Sheet
                                    key={i}
                                    variant={i === 0 ? 'outlined' : 'plain'}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 'md',
                                        bgcolor: i === 0 ? 'background.surface' : 'transparent',
                                        opacity: i === 0 ? 1 : 0.7,
                                    }}
                                >
                                    <Stack spacing={0.5}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography level="body-sm" fontWeight={600}>
                                                {entry.filename}
                                            </Typography>
                                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                {new Date(entry.date).toLocaleString('ru-RU')}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={2}>
                                            <Typography level="body-xs">
                                                Стр. {entry.startPage + 1}—{entry.endPage} ({entry.pageCount})
                                            </Typography>
                                            <Typography level="body-xs" color="success">
                                                +{entry.modulesFilled} модулей
                                            </Typography>
                                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                {entry.duration}с
                                            </Typography>
                                        </Stack>
                                        {i === 0 && (
                                            <Typography level="body-xs" sx={{ mt: 0.5, color: '#DA7756' }}>
                                                💡 Продолжить с страницы <strong>{entry.endPage + 1}</strong>
                                            </Typography>
                                        )}
                                    </Stack>
                                </Sheet>
                            ))}
                        </Stack>
                    )}
                </Sheet>
            )}

            {/* Config */}
            {pdfInfo && !results && (
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'xl' }}>
                    <Typography level="title-sm" fontWeight={600} sx={{ mb: 2 }}>
                        2. Настройки
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid xs={4}>
                            <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Начальная стр.
                            </Typography>
                            <Input
                                type="number"
                                size="sm"
                                value={startPage}
                                onChange={(e) => setStartPage(Math.max(0, parseInt(e.target.value) || 0))}
                                slotProps={{ input: { min: 0, max: pdfInfo.totalPages - 1 } }}
                            />
                        </Grid>
                        <Grid xs={4}>
                            <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Кол-во страниц
                            </Typography>
                            <Input
                                type="number"
                                size="sm"
                                value={totalPages}
                                onChange={(e) => setTotalPages(Math.max(1, parseInt(e.target.value) || 1))}
                                slotProps={{ input: { min: 1, max: pdfInfo.totalPages } }}
                            />
                        </Grid>
                        <Grid xs={4}>
                            <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Размер батча
                            </Typography>
                            <Input
                                type="number"
                                size="sm"
                                value={batchSize}
                                onChange={(e) => setBatchSize(Math.max(1, parseInt(e.target.value) || 1))}
                                slotProps={{ input: { min: 1, max: 30 } }}
                            />
                        </Grid>
                    </Grid>

                    <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
                        Страницы {startPage + 1}–{Math.min(startPage + totalPages, pdfInfo.totalPages)} ({Math.ceil(totalPages / batchSize)} батчей)
                    </Typography>

                    <Button
                        size="lg"
                        startDecorator={<Sparkle size={18} />}
                        onClick={handleGenerate}
                        loading={running}
                        fullWidth
                    >
                        Генерировать модули
                    </Button>

                    {running && (
                        <Box sx={{ mt: 2 }}>
                            <LinearProgress sx={{ mb: 1 }} />
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center' }}>
                                AI обрабатывает страницы... это может занять несколько минут
                            </Typography>
                        </Box>
                    )}
                </Sheet>
            )}

            {/* Results */}
            {results && (
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'xl' }}>
                    <Alert color="success" variant="soft" sx={{ mb: 2 }}>
                        Заполнено {results.modules_filled} модулей за {results.total_duration_sec}с
                    </Alert>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid xs={6} sm={3}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Батчей</Typography>
                            <Typography level="title-lg">{results.batches_processed}</Typography>
                        </Grid>
                        <Grid xs={6} sm={3}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Заполнено</Typography>
                            <Typography level="title-lg" color="success">{results.modules_filled}</Typography>
                        </Grid>
                        <Grid xs={6} sm={3}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Время</Typography>
                            <Typography level="title-lg">{results.total_duration_sec}с</Typography>
                        </Grid>
                        <Grid xs={6} sm={3}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Осталось</Typography>
                            <Typography level="title-lg">{results.remaining_empty}</Typography>
                        </Grid>
                    </Grid>

                    {/* Filled topics */}
                    {results.filled_topics?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography level="body-xs" fontWeight={600} sx={{ mb: 0.5 }}>
                                Заполненные темы:
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {results.filled_topics.map((t, i) => (
                                    <Chip key={i} size="sm" variant="soft" color="success">{t}</Chip>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Unmatched topics (AI found but couldn't match) */}
                    {results.unmatched_topics?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography level="body-xs" fontWeight={600} sx={{ mb: 0.5, color: 'warning.600' }}>
                                AI нашёл, но не смог сопоставить ({results.unmatched_topics.length}):
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {results.unmatched_topics.map((t, i) => (
                                    <Chip key={i} size="sm" variant="soft" color="warning">{t}</Chip>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Batch log */}
                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'md', maxHeight: 200, overflow: 'auto', mb: 2 }}>
                        {results.progress?.map((p, i) => (
                            <Typography key={i} level="body-xs" sx={{ fontFamily: 'monospace', mb: 0.25 }}>
                                #{p.batch} [{p.pages}] {p.duration_sec}с — {p.status === 'success'
                                    ? `+${p.filled_count} модулей`
                                    : p.status === 'skipped'
                                        ? `пропущен: ${p.reason}`
                                        : `ошибка: ${p.reason}`
                                }
                            </Typography>
                        ))}
                    </Sheet>

                    {/* Errors */}
                    {results.errors?.length > 0 && (
                        <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                            {results.errors.length} ошибок при генерации
                        </Alert>
                    )}

                    {/* Actions */}
                    <Stack direction="row" spacing={1.5}>
                        <Button
                            startDecorator={<ArrowsClockwise size={18} />}
                            onClick={handleContinue}
                        >
                            Продолжить (стр. {startPage + totalPages + 1}+)
                        </Button>
                        <Button
                            variant="outlined"
                            color="neutral"
                            onClick={() => {
                                setResults(null);
                                setPdfInfo(null);
                                setPdfPath('');
                            }}
                        >
                            Новый PDF
                        </Button>
                    </Stack>
                </Sheet>
            )}
        </Stack>
    );
}


// ========================================
// Batch Expand Panel (generate Explanations)
// ========================================
function BatchExpandPanel({ token }) {
    const [emptyStats, setEmptyStats] = useState(null);
    const [disciplineId, setDisciplineId] = useState(null);
    const [limit, setLimit] = useState(20);
    const [running, setRunning] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            const res = await APIGetEmptyModulesStats(token);
            if (res.success) setEmptyStats(res);
        } catch (e) {
            console.error('Error loading stats:', e);
        }
    };

    const handleExpand = async () => {
        setRunning(true);
        setError('');
        setResults(null);
        try {
            const data = { limit };
            if (disciplineId) data.discipline_id = disciplineId;
            const res = await APIBatchExpandModules(token, data);
            setResults(res);
            loadStats();
        } catch (e) {
            setError('Ошибка: ' + (e.response?.data?.error || e.message));
        } finally {
            setRunning(false);
        }
    };

    const expandPercent = emptyStats && emptyStats.filled > 0
        ? Math.round((emptyStats.expanded / emptyStats.filled) * 100)
        : 0;

    return (
        <Stack spacing={2.5}>
            {/* Stats */}
            {emptyStats && (
                <Sheet variant="soft" sx={{ p: 2, px: 2.5, borderRadius: 'lg' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography level="body-sm" fontWeight={500}>
                            Explanations сгенерировано
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            {emptyStats.expanded} из {emptyStats.filled} ({expandPercent}%)
                        </Typography>
                    </Stack>
                    <LinearProgress
                        determinate
                        value={expandPercent}
                        sx={{
                            '--LinearProgress-thickness': '6px',
                            '--LinearProgress-radius': '6px',
                        }}
                    />
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
                        {emptyStats.needs_expand} модулей ожидают генерации Explanations
                    </Typography>
                </Sheet>
            )}

            {error && <Alert color="danger" variant="soft">{error}</Alert>}

            {/* Config */}
            {!results && (
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'xl' }}>
                    <Typography level="title-sm" fontWeight={600} sx={{ mb: 2 }}>
                        Настройки
                    </Typography>

                    <Stack spacing={2}>
                        <Box>
                            <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Дисциплина
                            </Typography>
                            <Select
                                size="sm"
                                value={disciplineId}
                                onChange={(e, val) => setDisciplineId(val)}
                                placeholder="Все дисциплины"
                            >
                                <Option value={null}>Все дисциплины</Option>
                                {emptyStats?.by_discipline?.map(d => (
                                    <Option key={d.category_id} value={d.category_id}>
                                        {d.name} ({d.filled - (d.expanded || 0)} без Explanations)
                                    </Option>
                                ))}
                            </Select>
                        </Box>

                        <Box>
                            <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Количество за раз
                            </Typography>
                            <Input
                                type="number"
                                size="sm"
                                value={limit}
                                onChange={(e) => setLimit(Math.max(1, parseInt(e.target.value) || 1))}
                                slotProps={{ input: { min: 1, max: 100 } }}
                            />
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                                ~1-2 мин на модуль. {limit} модулей ≈ {limit}–{limit * 2} мин
                            </Typography>
                        </Box>

                        <Button
                            size="lg"
                            startDecorator={<Brain size={18} />}
                            onClick={handleExpand}
                            loading={running}
                            fullWidth
                        >
                            Сгенерировать Explanations
                        </Button>

                        {running && (
                            <Box>
                                <LinearProgress sx={{ mb: 1 }} />
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center' }}>
                                    AI расширяет модули... это может занять несколько минут
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Sheet>
            )}

            {/* Results */}
            {results && (
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'xl' }}>
                    <Alert color="success" variant="soft" sx={{ mb: 2 }}>
                        Расширено {results.expanded_count} модулей за {results.total_duration_sec}с
                    </Alert>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid xs={6} sm={3}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Расширено</Typography>
                            <Typography level="title-lg" color="success">{results.expanded_count}</Typography>
                        </Grid>
                        <Grid xs={6} sm={3}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Время</Typography>
                            <Typography level="title-lg">{results.total_duration_sec}с</Typography>
                        </Grid>
                        <Grid xs={6} sm={3}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Ошибок</Typography>
                            <Typography level="title-lg" color={results.errors?.length > 0 ? 'danger' : 'neutral'}>
                                {results.errors?.length || 0}
                            </Typography>
                        </Grid>
                        <Grid xs={6} sm={3}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Осталось</Typography>
                            <Typography level="title-lg">{results.remaining}</Typography>
                        </Grid>
                    </Grid>

                    {/* Expanded topics */}
                    {results.expanded_topics?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography level="body-xs" fontWeight={600} sx={{ mb: 0.5 }}>
                                Расширенные темы:
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {results.expanded_topics.map((t, i) => (
                                    <Chip key={i} size="sm" variant="soft" color="success">{t}</Chip>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Log */}
                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'md', maxHeight: 200, overflow: 'auto', mb: 2 }}>
                        {results.progress?.map((p, i) => (
                            <Typography key={i} level="body-xs" sx={{ fontFamily: 'monospace', mb: 0.25 }}>
                                #{p.index} {p.topic} — {p.status === 'success'
                                    ? `${p.original_len}→${p.expanded_len} (${p.duration_sec}с)`
                                    : `ошибка: ${p.reason}`
                                }
                            </Typography>
                        ))}
                    </Sheet>

                    {/* Errors */}
                    {results.errors?.length > 0 && (
                        <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                            {results.errors.length} ошибок при генерации
                        </Alert>
                    )}

                    {/* Actions */}
                    <Stack direction="row" spacing={1.5}>
                        {results.remaining > 0 && (
                            <Button
                                startDecorator={<ArrowsClockwise size={18} />}
                                onClick={() => setResults(null)}
                            >
                                Продолжить ({results.remaining} осталось)
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            color="neutral"
                            onClick={() => setResults(null)}
                        >
                            {results.remaining > 0 ? 'Настройки' : 'Готово'}
                        </Button>
                    </Stack>
                </Sheet>
            )}
        </Stack>
    );
}


// ========================================
// Text Generation Panel (admin only)
// ========================================
function TextGenerationPanel({ token, onGenerated }) {
    const [categoryId, setCategoryId] = useState('');
    const [sourceText, setSourceText] = useState('');
    const [generating, setGenerating] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleGenerate = async () => {
        if (!categoryId || !sourceText.trim()) return;
        setGenerating(true);
        setError('');
        setResult(null);
        try {
            const res = await APIGenerateModuleContent(token, {
                category_id: parseInt(categoryId),
                source_text: sourceText,
            });
            setResult(res);
            if (onGenerated) onGenerated();
        } catch (e) {
            setError('Ошибка: ' + (e.response?.data?.error || e.message));
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Stack spacing={2.5}>
            <Box>
                <Typography level="title-sm" sx={{ mb: 0.5 }}>Создать модуль из текста</Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    Вставь текст из учебника или лекции — AI структурирует его в модуль с объяснениями (тот же промпт, что и для PDF)
                </Typography>
            </Box>

            <FormControl>
                <FormLabel>ID категории</FormLabel>
                <Input
                    placeholder="например: 1312"
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    type="number"
                    sx={{ maxWidth: 220 }}
                />
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                    Найди нужный модуль в дереве → скопируй ID из URL (/modules/ID)
                </Typography>
            </FormControl>

            <FormControl>
                <FormLabel>Исходный текст</FormLabel>
                <Textarea
                    placeholder="Вставь текст из учебника, First Aid, лекций..."
                    value={sourceText}
                    onChange={(e) => setSourceText(e.target.value)}
                    minRows={8}
                    maxRows={20}
                    sx={{ fontFamily: 'inherit', fontSize: 'sm' }}
                />
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                    {sourceText.length} символов
                </Typography>
            </FormControl>

            {error && (
                <Alert color="danger" size="sm">{error}</Alert>
            )}

            {result?.success && (
                <Alert color="success" size="sm">
                    <Stack spacing={0.5}>
                        <Typography level="body-sm" fontWeight={600}>Модуль создан!</Typography>
                        <Typography level="body-xs">{result.module?.title}</Typography>
                        <Button
                            component={RouterLink}
                            to={`/modules/${result.module?.id}`}
                            size="sm"
                            variant="plain"
                            color="success"
                            sx={{ alignSelf: 'flex-start', px: 0 }}
                        >
                            Открыть модуль →
                        </Button>
                    </Stack>
                </Alert>
            )}

            <Button
                loading={generating}
                disabled={!categoryId || !sourceText.trim()}
                onClick={handleGenerate}
                sx={{ alignSelf: 'flex-start' }}
            >
                {generating ? 'Генерация...' : 'Создать модуль'}
            </Button>
        </Stack>
    );
}


// ========================================
// Main Page
// ========================================
export default function ModulesPage() {
    const { token, user } = useContext(LayoutContext);
    const [tree, setTree] = useState([]);
    const [stats, setStats] = useState({ total_modules: 0, total_read: 0 });
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [contentMatchIds, setContentMatchIds] = useState(new Set());
    const [contentSearching, setContentSearching] = useState(false);
    const [libraryLocked, setLibraryLocked] = useState(false);
    const navigate = useNavigate();

    const isAdmin = user?.role === 'admin';
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        loadModules();
    }, []);

    useEffect(() => {
        if (!searchQuery || searchQuery.length < 3) {
            setContentMatchIds(new Set());
            return;
        }
        const timer = setTimeout(async () => {
            setContentSearching(true);
            try {
                const res = await APISearchModulesContent(token, searchQuery);
                if (res.success) setContentMatchIds(new Set(res.ids));
            } catch (e) {
                // fail silently
            } finally {
                setContentSearching(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery, token]);

    const loadModules = async () => {
        try {
            const response = await APIGetModules(token);
            if (response.success) {
                setTree(response.tree || []);
                setStats(response.stats || { total_modules: 0, total_read: 0 });
            }
        } catch (error) {
            if (error.response?.data?.error === 'library_locked') {
                setLibraryLocked(true);
            } else {
                console.error('Error loading modules:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    // Separate IM Residency from regular disciplines
    const residencyDiscipline = isAdmin ? tree.find(d => d.name === 'IM Residency') : null;
    const regularTree = tree.filter(d => d.name !== 'IM Residency');

    const filteredTree = searchQuery
        ? regularTree.filter(disc =>
            disc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            disc.subsections.some(sub =>
                sub.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                sub.topics.some(t =>
                    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    contentMatchIds.has(t.id)
                )
            )
        )
        : regularTree;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (libraryLocked) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 600, mx: 'auto', textAlign: 'center', mt: 8 }}>
                <Lock size={56} />
                <Typography level="h3" sx={{ mb: 1 }}>Библиотека закрыта</Typography>
                <Typography level="body-md" color="neutral" sx={{ mb: 3 }}>
                    Доступ к 1 200+ учебным модулям открывается на платном тарифе.
                </Typography>
                <Button component={RouterLink} to="/" size="lg"
                    sx={{ bgcolor: '#DA7756', color: '#fff', '&:hover': { bgcolor: '#c4633f' } }}>
                    Перейти к тарифам
                </Button>
            </Box>
        );
    }

    const overallProgress = stats.total_modules > 0
        ? Math.round((stats.total_read / stats.total_modules) * 100)
        : 0;

    // === Tree content (shared between admin tab and non-admin view) ===
    const treeContent = (
        <>
            {/* Stats bar */}
            {stats.total_modules > 0 && (
                <Sheet variant="soft" sx={{ p: 2, px: 2.5, borderRadius: 'lg', mb: 3 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography level="body-sm" fontWeight={500}>
                            Прогресс
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            {stats.total_read} из {stats.total_modules} прочитано
                        </Typography>
                    </Stack>
                    <LinearProgress
                        determinate
                        value={overallProgress}
                        sx={{
                            '--LinearProgress-thickness': '6px',
                            '--LinearProgress-radius': '6px',
                        }}
                    />
                </Sheet>
            )}

            {/* Search */}
            <Input
                placeholder="Поиск по темам и тексту модулей..."
                startDecorator={contentSearching
                    ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '16px' }} />
                    : <MagnifyingGlass size={18} />
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                endDecorator={searchQuery && contentMatchIds.size > 0 && (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', whiteSpace: 'nowrap' }}>
                        {contentMatchIds.size} в тексте
                    </Typography>
                )}
                sx={{ mb: 3, '--Input-focusedThickness': '1px' }}
            />

            {/* Discipline Tree */}
            {regularTree.length === 0 ? (
                <Sheet variant="outlined" sx={{ p: 5, borderRadius: 'xl', textAlign: 'center' }}>
                    <BookOpen size={48} />
                    <Typography level="title-md" sx={{ mb: 0.5 }}>Модули пока не добавлены</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        {isAdmin
                            ? 'Импортируйте структуру через manage.py import_checklist'
                            : 'Модули появятся здесь, когда администратор их добавит.'
                        }
                    </Typography>
                </Sheet>
            ) : filteredTree.length === 0 ? (
                <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                    Ничего не найдено по запросу «{searchQuery}»
                </Typography>
            ) : (
                <Stack spacing={1.5}>
                    {filteredTree.map(discipline => (
                        <DisciplineCard
                            key={discipline.category_id}
                            discipline={discipline}
                            searchQuery={searchQuery}
                            contentMatchIds={contentMatchIds}
                            navigate={navigate}
                        />
                    ))}
                </Stack>
            )}
        </>
    );

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: 'auto' }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                <BookOpen size={28} />
                <Typography level="h2">Библиотека</Typography>
            </Stack>

            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                High Yield сборник фактов и объяснений для медицинского образования
            </Typography>

            {/* IM Residency banner (admin only) */}
            {residencyDiscipline && (
                <Box
                    component={RouterLink}
                    to="/residency"
                    sx={{
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        px: 2.5,
                        mb: 3,
                        borderRadius: 'xl',
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2c29 100%)',
                        cursor: 'pointer',
                        transition: 'opacity 0.15s ease, transform 0.15s ease',
                        '&:hover': { opacity: 0.92, transform: 'translateY(-1px)' },
                        border: '1px solid rgba(255,255,255,0.08)',
                    }}
                >
                    <Box sx={{
                        width: 42, height: 42, borderRadius: 'lg',
                        bgcolor: 'rgba(218,119,86,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Stethoscope size={22} color="#DA7756" weight="duotone" />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography level="title-sm" sx={{ color: '#fff', fontWeight: 700 }}>
                            IM Residency
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {residencyDiscipline.subsections?.length || 0} {residencyDiscipline.subsections?.length === 1 ? 'книга' : 'книги'} · {residencyDiscipline.module_count} глав
                        </Typography>
                    </Box>
                    <ArrowRight size={18} color="rgba(255,255,255,0.5)" />
                </Box>
            )}

            {/* Admin: tabs | Non-admin: tree only */}
            {isAdmin ? (
                <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ bgcolor: 'transparent' }}>
                    <TabList disableUnderline sx={{ gap: 1, mb: 2.5 }}>
                        <Tab variant="plain" sx={{ fontSize: 'sm', fontWeight: 500, borderRadius: 'md' }}>
                            <BookOpen size={16} style={{ marginRight: 4 }} />
                            Модули
                        </Tab>
                        <Tab variant="plain" sx={{ fontSize: 'sm', fontWeight: 500, borderRadius: 'md' }}>
                            <Sparkle size={16} style={{ marginRight: 4 }} />
                            High Yield из PDF
                        </Tab>
                        <Tab variant="plain" sx={{ fontSize: 'sm', fontWeight: 500, borderRadius: 'md' }}>
                            <Brain size={16} style={{ marginRight: 4 }} />
                            Explanations
                        </Tab>
                        <Tab variant="plain" sx={{ fontSize: 'sm', fontWeight: 500, borderRadius: 'md' }}>
                            <TextT size={16} style={{ marginRight: 4 }} />
                            Из текста
                        </Tab>
                    </TabList>

                    <TabPanel value={0} sx={{ p: 0 }}>
                        {treeContent}
                    </TabPanel>

                    <TabPanel value={1} sx={{ p: 0 }}>
                        <BatchGenerationPanel token={token} onGenerated={() => { loadModules(); }} />
                    </TabPanel>

                    <TabPanel value={2} sx={{ p: 0 }}>
                        <BatchExpandPanel token={token} />
                    </TabPanel>

                    <TabPanel value={3} sx={{ p: 0 }}>
                        <TextGenerationPanel token={token} onGenerated={() => { loadModules(); }} />
                    </TabPanel>
                </Tabs>
            ) : (
                treeContent
            )}
        </Box>
    );
}
